// client/src/store/products_config/validationSchema.js
import * as yup from "yup";
import { CATEGORIES } from "./utils/Categories";

const DEFAULTS = {
   priceDigits: 2,
   maxPrice: 1_000_000,
   maxDimension: 100_000, // cm
   maxWeight: 100_000,    // kg
   maxPhotos: 5,
   maxPhotoMB: 10,
   maxVideoMB: 200,
   allowedVideoMime: ["video/mp4", "video/quicktime", "video/webm"],
};

const BYTES = (mb) => mb * 1024 * 1024;
const hasFileCtor = typeof File !== "undefined";
const isFile = (v) => hasFileCtor && v instanceof File;
const isImageMimeSafe = (mime = "") =>
  mime.startsWith("image/") && mime.toLowerCase() !== "image/svg+xml";
const ensureHttps = (url = "") => /^https:\/\//i.test(url);

// ---- category helpers
const isLeaf = (ids = []) => {
  if (!Array.isArray(ids) || !ids.length) return false;
  let nodes = CATEGORIES;
  for (let i = 0; i < ids.length; i++) {
    const idStr = String(ids[i]);
    const n = nodes?.find((x) => String(x.id) === idStr);
    if (!n) return false;
    if (i === ids.length - 1) return !n.children?.length;
    nodes = n.children;
  }
  return false;
};

// ---- text safety
const CONTROL_RE = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/;
const BIDI_RE = /[\u202A-\u202E\u2066-\u2069]/;
const HTMLY_RE = /[<>]/;

const normalizeSingleLine = (s) => (s || "").replace(/\s+/g, " ").trim();
const normalizeMultiline = (s) => {
  if (!s) return "";
  const collapsed = s.replace(/[ \t\f\v]+/g, " ");
  return collapsed.split("\n").map((ln) => ln.trimEnd()).join("\n").trim();
};

function safeTextSingle(max) {
  return yup
    .string()
    .transform((v) => normalizeSingleLine(v))
    .max(max, `Must be at most ${max} characters.`)
    .test("no-control", "Contains disallowed control characters.", (v) => !v || !CONTROL_RE.test(v))
    .test("no-bidi", "Contains disallowed directionality controls.", (v) => !v || !BIDI_RE.test(v))
    .test("no-html", "Please remove raw HTML tags.", (v) => !v || !HTMLY_RE.test(v));
}

function safeTextMulti(max) {
  return yup
    .string()
    .transform((v) => normalizeMultiline(v))
    .max(max, `Must be at most ${max} characters.`)
    .test("no-control", "Contains disallowed control characters.", (v) => !v || !CONTROL_RE.test(v))
    .test("no-bidi", "Contains disallowed directionality controls.", (v) => !v || !BIDI_RE.test(v))
    .test("no-html", "Please remove raw HTML tags.", (v) => !v || !HTMLY_RE.test(v));
}

// ---- numbers
const parseMoneyLike = (orig) => {
  if (typeof orig === "number") return orig;
  if (typeof orig !== "string") return NaN;
  const s = orig.replace(/\s/g, "").replace(/,/g, ".");
  const cleaned = s.replace(/(?!^)-|[^0-9.-]/g, "");
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : NaN;
};

const nonNeg = () => yup.number().typeError("Must be a number").min(0, "Must be ≥ 0");

const withMaxDecimals = (schema, maxDecimals) =>
  schema.test(
    "max-decimals",
    `At most ${maxDecimals} decimal place${maxDecimals === 1 ? "" : "s"} allowed.`,
    (v, ctx) => {
      if (v == null || !isFinite(v)) return true;
      const orig = ctx.originalValue;
      const str = typeof orig === "string" ? orig.replace(/,/g, ".") : String(v);
      const m = str.match(/\.(\d+)/);
      const places = m ? m[1].length : 0;
      return places <= maxDecimals;
    }
  );

// ---- media
function photoItemSchema({ maxPhotoMB }) {
  return yup
    .object({
      id: yup.string().nullable(),
      photo_url: yup
        .string()
        .nullable()
        .test("url-or-null", "Invalid URL.", (v) => !v || yup.string().url().isValidSync(v))
        .test("https-only", "Image URL must be HTTPS.", (v) => !v || ensureHttps(v)),
      file: yup
        .mixed()
        .test("type", "Photo must be a valid image file.", (v) => !v || !isFile(v) || isImageMimeSafe(v.type))
        .test("size", `Photo must be ≤ ${maxPhotoMB} MB.`, (v) => !v || !isFile(v) || v.size <= BYTES(maxPhotoMB)),
    })
    .test("one-source", "Provide either a file OR a URL (not both).", (obj) => {
      if (!obj) return true;
      const hasUrl = !!obj.photo_url;
      const hasFile = isFile(obj.file);
      return !(hasUrl && hasFile);
    })
    .test("present", "Missing file or URL for photo.", (obj) => {
      if (!obj) return true;
      return !!obj.photo_url || isFile(obj.file) || !!obj.id;
    });
}

function videoObjectSchema({ maxVideoMB, allowedVideoMime }) {
  const allowedMsg = `Supported types: ${allowedVideoMime.join(", ")}.`;
  return yup
    .object({
      id: yup.string().nullable(),
      video_url: yup
        .string()
        .nullable()
        .test("url-or-null", "Invalid URL.", (v) => !v || yup.string().url().isValidSync(v))
        .test("https-only", "Video URL must be HTTPS.", (v) => !v || ensureHttps(v)),
      file: yup
        .mixed()
        .test("type", `Unsupported video type. ${allowedMsg}`, (v) => {
          if (!v || !isFile(v)) return true;
          return allowedVideoMime.includes((v.type || "").toLowerCase());
        })
        .test("size", `Video must be ≤ ${maxVideoMB} MB.`, (v) => !v || !isFile(v) || v.size <= BYTES(maxVideoMB)),
    })
    .nullable()
    .test("one-source", "Provide either a file OR a URL (not both).", (obj) => {
      if (!obj) return true;
      const hasUrl = !!obj.video_url;
      const hasFile = isFile(obj.file);
      return !(hasUrl && hasFile);
    });
}
const coerceEmpty = (s) =>
  s.transform((val, orig) => (orig === "" || val === null || Number.isNaN(val) ? undefined : val));

const intPos = () =>
  coerceEmpty(yup.number().typeError("Must be a number"))
    .integer("Must be a whole number")
    .min(1, "Must be ≥ 1");

// ---- factory
export function makeProductSchema(opts = {}) {
  const cfg = { ...DEFAULTS, ...opts };

  const MONEY = withMaxDecimals(
    nonNeg()
      // Make empty string/undefined become undefined so "required" can fire,
      // and so clearing the input clears the error.
      .transform((v, orig) => (orig === "" || orig == null ? undefined : parseMoneyLike(orig)))
      .max(cfg.maxPrice, `Must be ≤ ${cfg.maxPrice}`),
    cfg.priceDigits
  );

  const DIM = withMaxDecimals(
    nonNeg()
      // Optional fields: treat empty as undefined (no error).
      .transform((v, orig) => (orig === "" || orig == null ? undefined : parseMoneyLike(orig)))
      .max(cfg.maxDimension, `Must be ≤ ${cfg.maxDimension}`),
    2
  );

  const WEIGHT = withMaxDecimals(
    nonNeg()
      // Optional fields: treat empty as undefined (no error).
      .transform((v, orig) => (orig === "" || orig == null ? undefined : parseMoneyLike(orig)))
      .max(cfg.maxWeight, `Must be ≤ ${cfg.maxWeight}`),
    3
  );

  return yup
    .object({
      // form-only extras we strip so noUnknown() won’t fail:
      id: yup.mixed().strip(),
      isNew: yup.mixed().strip(),
      status: yup.mixed().strip(),

      name: safeTextSingle(120).required("Product name is required"),
      description: safeTextMulti(4000).required("Description is required"),
      type: yup.mixed().oneOf(["physical", "digital", "service"]).required("Product type is required"),

      category: yup
        .array()
        .of(
          yup
            .mixed()
            .test("is-intlike", "Invalid category id.", (v) => v != null && /^\d+$/.test(String(v)))
            .transform((v) => (v == null ? v : Number(v)))
        )
        .max(10, "Category path too deep.")
        .min(1, "Category is required.")
        .test("leaf", "Pick a specific subcategory.", (ids) => isLeaf(ids))
        .default([]),

      price: MONEY.required("Price required"),

      // IMPORTANT: in older Yup versions, then/otherwise MUST be functions.
      weight: yup.mixed().when("type", {
        is: "physical",
        then: () => WEIGHT.required("Weight is required"),
        otherwise: (schema) => schema.strip(),
      }),
      width: yup.mixed().when("type", {
        is: "physical",
        then: () => DIM.required("Width is required"),
        otherwise: (schema) => schema.strip(),
      }),
      height: yup.mixed().when("type", {
        is: "physical",
        then: () => DIM.required("Height is required"),
        otherwise: (schema) => schema.strip(),
      }),
      depth: yup.mixed().when("type", {
        is: "physical",
        then: () => DIM.required("Depth is required"),
        otherwise: (schema) => schema.strip(),
      }),

      photos: yup
        .array()
        .of(photoItemSchema({ maxPhotoMB: cfg.maxPhotoMB }))
        .max(cfg.maxPhotos, `Up to ${cfg.maxPhotos} photos`)
        .min(1, "At least one photo is required.")
        .test("unique-urls", "Duplicate photo URLs detected.", (arr) => {
          if (!arr) return true;
          const urls = arr.map((x) => (x?.photo_url || "").trim()).filter(Boolean);
          return urls.length === new Set(urls).size;
        })
        .default([]),

      video: videoObjectSchema({
        maxVideoMB: cfg.maxVideoMB,
        allowedVideoMime: cfg.allowedVideoMime,
      }).default(null),

      currency: yup.mixed().strip(),
      ageRestricted: yup.boolean().default(false),
      ageRestrictedMin: intPos()
        .max(25, "Must be ≤ 25")
        .when("ageRestricted", {
          is: true,
          then: (s) => s.required("Minimum age is required when restriction is on"),
          otherwise: (s) => s.strip(), // remove from payload if toggle is off
        }),
    })
    .noUnknown(true, "Contains unexpected fields.");
}

const schema = makeProductSchema();
export default schema;
